import { useEffect, useState, type FormEvent } from 'react'
import { api, type NachfrageEinstellung, type NachfrageRhythmus, type Profil, type PushStand, type Schreibweise } from '../api'
import { pushAktiv, pushAktivieren, pushDeaktivieren, pushErneuern, pushMoeglich } from '../push'

export function Konto({ benutzername, admin, onAbgemeldet }: { benutzername: string; admin: boolean; onAbgemeldet: () => void }) {
  const [alt, setAlt] = useState('')
  const [neu, setNeu] = useState('')
  const [wiederholung, setWiederholung] = useState('')
  const [meldung, setMeldung] = useState<{ text: string; fehler: boolean } | null>(null)

  const passwortAendern = async (e: FormEvent) => {
    e.preventDefault()
    if (neu !== wiederholung) return setMeldung({ text: 'Die beiden neuen Passwörter stimmen nicht überein.', fehler: true })
    try {
      const { abgemeldet } = await api.passwortAendern(alt, neu)
      setAlt('')
      setNeu('')
      setWiederholung('')
      setMeldung({ text: `Passwort geändert.${abgemeldet ? ` ${abgemeldet} andere Sitzung(en) abgemeldet.` : ''}`, fehler: false })
    } catch (err) {
      setMeldung({ text: (err as Error).message, fehler: true })
    }
  }

  const andereAbmelden = async () => {
    const { abgemeldet } = await api.andereAbmelden()
    setMeldung({ text: abgemeldet ? `${abgemeldet} andere Sitzung(en) abgemeldet.` : 'Es gab keine anderen Sitzungen.', fehler: false })
  }

  return (
    <div className="konto">
      <section className="karte">
        <h2>{benutzername}</h2>
        <p className="leise">{admin ? 'Administrator' : 'Benutzer'} · dein Tagebuch ist nur für dich sichtbar</p>
        <div className="knoepfe links">
          <a className="knopf zweitrangig" href="#freunde">
            Freunde
          </a>
          {admin && (
            <a className="knopf zweitrangig" href="#verwaltung">
              Benutzer verwalten
            </a>
          )}
          <button onClick={() => api.abmelden().then(onAbgemeldet)}>Abmelden</button>
        </div>
      </section>

      {meldung && <p className={meldung.fehler ? 'hinweis fehler' : 'hinweis'}>{meldung.text}</p>}

      <EmailBereich />

      <BenachrichtigungenBereich />

      <NachfragenBereich />

      <form className="karte formular" onSubmit={passwortAendern}>
        <h3>Passwort ändern</h3>
        <input type="text" autoComplete="username" value={benutzername} readOnly hidden />
        <label>
          Bisheriges Passwort
          <input type="password" autoComplete="current-password" value={alt} onChange={(e) => setAlt(e.target.value)} required />
        </label>
        <label>
          Neues Passwort (mindestens 10 Zeichen)
          <input type="password" autoComplete="new-password" minLength={10} value={neu} onChange={(e) => setNeu(e.target.value)} required />
        </label>
        <label>
          Neues Passwort wiederholen
          <input type="password" autoComplete="new-password" value={wiederholung} onChange={(e) => setWiederholung(e.target.value)} required />
        </label>
        <p className="leise klein">Dabei werden alle anderen Geräte abgemeldet.</p>
        <div className="knoepfe">
          <button type="submit">Passwort ändern</button>
        </div>
      </form>

      <SchreibweisenBereich />

      <PruefBereich />

      <section className="karte">
        <h3>Andere Geräte</h3>
        <p className="leise klein">Handy verloren oder an einem fremden Rechner angemeldet? Hier meldest du alle anderen Sitzungen ab.</p>
        <button className="zweitrangig" onClick={andereAbmelden}>
          Auf allen anderen Geräten abmelden
        </button>
      </section>
    </div>
  )
}

/** E-Mail-Adresse: noetig fuer "Passwort vergessen"; eine neue Adresse gilt erst nach Bestaetigung per Link. */
function EmailBereich() {
  const [profil, setProfil] = useState<Profil | null>(null)
  const [email, setEmail] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)

  const laden = () => api.profil().then(setProfil).catch((e) => setFehler((e as Error).message))
  useEffect(() => {
    laden()
  }, [])

  const absenden = async (e: FormEvent) => {
    e.preventDefault()
    setFehler(null)
    try {
      await api.emailAendern(email)
      setEmail('')
      await laden()
    } catch (err) {
      setFehler((err as Error).message)
    }
  }

  if (!profil) return fehler ? <p className="hinweis fehler">{fehler}</p> : null

  return (
    <form className="karte formular" onSubmit={absenden}>
      <h3>E-Mail-Adresse</h3>
      {profil.email ? (
        <p>
          {profil.email} <span className={profil.emailVerifiziert ? 'chip' : 'chip gesperrt'}>{profil.emailVerifiziert ? 'bestätigt' : 'nicht bestätigt'}</span>
        </p>
      ) : (
        <p className="leise klein">Keine E-Mail-Adresse hinterlegt. Ohne bestätigte Adresse kannst du dein Passwort nicht selbst zurücksetzen.</p>
      )}
      {profil.ausstehendeEmail && (
        <p className="hinweis">Bestätigungslink an {profil.ausstehendeEmail} verschickt. Die neue Adresse gilt, sobald du den Link aufgerufen hast.</p>
      )}
      <label>
        {profil.email ? 'Neue E-Mail-Adresse' : 'E-Mail-Adresse'}
        <input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
      </label>
      {fehler && <p className="fehler">{fehler}</p>}
      <div className="knoepfe">
        <button type="submit" disabled={!email}>
          Bestätigungslink senden
        </button>
      </div>
    </form>
  )
}

/** Stichhaltigkeitsprüfung aller Notizen nachträglich anstoßen; der Server arbeitet sie im Hintergrund ab. */
function PruefBereich() {
  const [offen, setOffen] = useState<number | null>(null)
  const [meldung, setMeldung] = useState<{ text: string; fehler: boolean } | null>(null)

  useEffect(() => {
    api.pruefStand().then((s) => setOffen(s.offen)).catch(() => {})
  }, [])

  // Solange geprüft wird, den Fortschritt nachladen
  useEffect(() => {
    if (!offen) return
    const t = setTimeout(() => {
      api
        .pruefStand()
        .then((s) => {
          setOffen(s.offen)
          if (!s.offen) setMeldung({ text: 'Fertig. Neue Rückfragen findest du bei den Personen.', fehler: false })
        })
        .catch(() => {})
    }, 5000)
    return () => clearTimeout(t)
  }, [offen])

  const starten = async () => {
    if (!confirm('Alle Notizen mit deinen übrigen Notizen und der Kartei abgleichen? Das dauert je Notiz einige Sekunden und kostet je Notiz einen KI-Aufruf.')) return
    setMeldung(null)
    try {
      const s = await api.allePruefen()
      setOffen(s.offen)
      if (!s.offen) setMeldung({ text: 'Es gibt keine Notizen, die sich prüfen ließen.', fehler: false })
    } catch (e) {
      setMeldung({ text: (e as Error).message, fehler: true })
    }
  }

  return (
    <section className="karte">
      <h3>Stichhaltigkeit</h3>
      <p className="leise klein">
        Neue Notizen werden automatisch mit dem abgeglichen, was schon bekannt ist. Hier prüfst du alle bisherigen Notizen nachträglich, z. B.
        ältere, die vor dieser Prüfung entstanden sind. Archivierte Notizen bleiben außen vor; einzelne Notizen prüfst du über „Prüfen“ an der
        Notiz.
      </p>
      {!!offen && <p className="hinweis">Wird geprüft … noch {offen === 1 ? 'eine Notiz' : `${offen} Notizen`}.</p>}
      {meldung && <p className={meldung.fehler ? 'fehler' : 'hinweis'}>{meldung.text}</p>}
      <button className="zweitrangig" onClick={starten} disabled={!!offen}>
        Alle Notizen prüfen
      </button>
    </section>
  )
}

/** Gemerkte Schreibweisen: wirken auf neue Transkripte; abgelehnte verhindern nur, dass der Vorschlag wiederkommt. */
function SchreibweisenBereich() {
  const [liste, setListe] = useState<Schreibweise[] | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)

  useEffect(() => {
    api.schreibweisen().then(setListe).catch((e) => setFehler((e as Error).message))
  }, [])

  if (!liste?.length && !fehler) return null
  return (
    <section className="karte">
      <h3>Gemerkte Schreibweisen</h3>
      <p className="leise klein">Neue Aufnahmen werden damit richtig geschrieben. Abgelehnte schlägt die App nicht wieder vor.</p>
      {fehler && <p className="fehler">{fehler}</p>}
      <ul className="fakten-liste">
        {liste?.map((s) => (
          <li key={s.id}>
            <span>
              {s.abgelehnt ? (
                <>
                  „{s.falsch}“ bleibt <span className="leise klein">(nicht „{s.richtig}“)</span>
                </>
              ) : (
                <>
                  „{s.falsch}“ → „{s.richtig}“
                </>
              )}
            </span>
            <button
              className="link gefahr"
              aria-label="Schreibweise vergessen"
              onClick={() =>
                api
                  .schreibweiseVergessen(s.id)
                  .then(() => setListe(liste.filter((x) => x.id !== s.id)))
                  .catch((e) => setFehler((e as Error).message))
              }
            >
              ✕
            </button>
          </li>
        ))}
      </ul>
    </section>
  )
}

/** Web-Push für Erinnerungen: pro Gerät ein- und ausschalten, mit Test */
function BenachrichtigungenBereich() {
  const [stand, setStand] = useState<PushStand | null>(null)
  const [aktiv, setAktiv] = useState(false)
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<{ text: string; fehler: boolean } | null>(null)

  const laden = () => {
    api.pushStand().then(setStand).catch(() => {})
    pushAktiv().then(setAktiv).catch(() => setAktiv(false))
  }
  useEffect(laden, [])
  useEffect(() => {
    pushErneuern().catch(() => {})
  }, [])

  const aktion = async (f: () => Promise<string | void>) => {
    setLaeuft(true)
    setMeldung(null)
    try {
      const text = await f()
      if (text) setMeldung({ text, fehler: false })
    } catch (e) {
      setMeldung({ text: (e as Error).message, fehler: true })
    }
    laden()
    setLaeuft(false)
  }

  return (
    <section className="karte">
      <h3>Benachrichtigungen</h3>
      {!stand?.schluessel ? (
        <p className="leise">Auf dem Server sind Benachrichtigungen noch nicht eingerichtet. Erinnerungen stehen trotzdem unter „Termine“.</p>
      ) : !pushMoeglich() ? (
        <p className="leise">
          Dieses Gerät oder dieser Browser kann keine Benachrichtigungen zeigen. Auf dem iPhone geht es nur, wenn meine Perlen zum
          Home-Bildschirm hinzugefügt ist.
        </p>
      ) : (
        <>
          <p className="leise">
            {aktiv
              ? 'Dieses Gerät meldet sich, wenn eine Erinnerung fällig ist.'
              : 'Schalte das ein, damit dieses Gerät sich meldet, wenn eine Erinnerung fällig ist, auch wenn die App zu ist.'}
            {stand.geraete > 0 && ` Angemeldete Geräte: ${stand.geraete}.`}
          </p>
          <div className="knoepfe links">
            {aktiv ? (
              <>
                <button
                  className="zweitrangig"
                  disabled={laeuft}
                  onClick={() =>
                    aktion(async () => {
                      const r = await api.pushTest()
                      return r.zugestellt > 0 ? 'Test gesendet. Es sollte gleich eine Benachrichtigung erscheinen.' : 'Der Push-Dienst hat den Test nicht angenommen.'
                    })
                  }
                >
                  Test senden
                </button>
                <button className="zweitrangig" disabled={laeuft} onClick={() => aktion(pushDeaktivieren)}>
                  Ausschalten
                </button>
              </>
            ) : (
              <button disabled={laeuft} onClick={() => aktion(pushAktivieren)}>
                Auf diesem Gerät einschalten
              </button>
            )}
          </div>
          {meldung && <p className={meldung.fehler ? 'fehler' : 'leise'}>{meldung.text}</p>}
        </>
      )}
    </section>
  )
}

const RHYTHMEN: { wert: NachfrageRhythmus; text: string }[] = [
  { wert: 'AUS', text: 'Aus' },
  { wert: 'TAEGLICH', text: 'Täglich' },
  { wert: 'ALLE_3_TAGE', text: 'Alle 3 Tage' },
  { wert: 'WOECHENTLICH', text: 'Einmal pro Woche' },
]

/** Optional: Die App stellt in Abständen eine Frage zu dem, was du gespeichert hast. Standard: aus. */
function NachfragenBereich() {
  const [e, setE] = useState<NachfrageEinstellung | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [meldung, setMeldung] = useState<{ text: string; fehler: boolean } | null>(null)

  useEffect(() => {
    api.nachfrageEinstellung().then(setE).catch(() => {})
  }, [])
  if (!e) return null

  const ausfuehren = async (f: () => Promise<string>) => {
    setLaeuft(true)
    setMeldung(null)
    try {
      setMeldung({ text: await f(), fehler: false })
    } catch (ex) {
      setMeldung({ text: (ex as Error).message, fehler: true })
    }
    setLaeuft(false)
  }

  return (
    <form
      className="karte formular"
      onSubmit={(ev) => {
        ev.preventDefault()
        ausfuehren(async () => {
          const neu = await api.nachfrageEinstellungSpeichern({ ...e, zeitzone: Intl.DateTimeFormat().resolvedOptions().timeZone })
          setE(neu)
          return neu.rhythmus === 'AUS' ? 'Nachfragen sind aus.' : 'Gespeichert. Die erste Frage kommt zur eingestellten Uhrzeit.'
        })
      }}
    >
      <h3>Nachfragen</h3>
      <p className="leise klein">
        Die App stellt dir in Abständen eine Frage zu dem, was du schon gespeichert hast: als Gedächtnisstütze („Weißt du noch …?“, auf
        Wunsch mit Auflösung) oder als möglicher Zusammenhang zwischen alten Notizen. Sie steht oben im Tagebuch; deine Antwort wird eine
        normale Notiz. Zur Auswahl der Frage sieht die KI Auszüge deiner Notizen, wie bei „Fragen“.
      </p>
      <label>
        Wie oft?
        <select value={e.rhythmus} onChange={(ev) => setE({ ...e, rhythmus: ev.target.value as NachfrageRhythmus })}>
          {RHYTHMEN.map((r) => (
            <option key={r.wert} value={r.wert}>
              {r.text}
            </option>
          ))}
        </select>
      </label>
      {e.rhythmus !== 'AUS' && (
        <>
          <label>
            Um wie viel Uhr?
            <input type="time" value={e.uhrzeit} onChange={(ev) => setE({ ...e, uhrzeit: ev.target.value })} required />
          </label>
          <label className="auswahl-zeile">
            <input type="checkbox" checked={e.weckruf} onChange={(ev) => setE({ ...e, weckruf: ev.target.checked })} />
            Mit Benachrichtigung (braucht eingeschaltete Benachrichtigungen oben)
          </label>
        </>
      )}
      {meldung && <p className={meldung.fehler ? 'fehler' : 'leise'}>{meldung.text}</p>}
      <div className="knoepfe">
        <button
          type="button"
          className="zweitrangig"
          disabled={laeuft}
          onClick={() =>
            ausfuehren(async () => ((await api.nachfrageJetzt()) ? 'Die Frage steht oben im Tagebuch.' : 'Mir fällt gerade keine Frage ein. Erzähl erst ein paar Notizen.'))
          }
        >
          Jetzt eine Frage stellen
        </button>
        <button type="submit" disabled={laeuft || !e.uhrzeit}>
          Speichern
        </button>
      </div>
    </form>
  )
}
